import { useCallback, useEffect, useRef, useState } from 'react';
import { smsSendAPI, smsVerifyAPI } from '../../api/login';

const digits = (v) => String(v || '').replace(/\D/g, '');
export const isMobilePhone = (v) => /^01[016789]\d{7,8}$/.test(digits(v));

// 휴대폰 인증 상태 — 번호 입력 → 인증번호 발송(sent, 남은 시간 카운트) → 확인(verified → onVerified(uid, phone))
export default function usePhoneVerify({ purpose = '', onVerified, initialPhone = '' }) {
  const [phone, setPhone] = useState(initialPhone);
  const [code, setCode] = useState('');
  const [step, setStep] = useState('idle');     // idle | sent | verified
  const [busy, setBusy] = useState(false);
  const [remain, setRemain] = useState(0);      // 인증번호 남은 시간(초)
  const [error, setError] = useState('');
  const timer = useRef(null);

  const stopTimer = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }, []);

  const startTimer = useCallback((sec) => {
    stopTimer();
    setRemain(sec);
    timer.current = setInterval(() => {
      setRemain((r) => {
        if (r <= 1) { stopTimer(); return 0; }
        return r - 1;
      });
    }, 1000);
  }, [stopTimer]);

  useEffect(() => stopTimer, [stopTimer]);

  const send = useCallback(async () => {
    const p = digits(phone);
    if (!isMobilePhone(p)) { setError('휴대폰 번호를 확인해 주세요.'); return; }
    setBusy(true);
    setError('');
    const resp = await smsSendAPI({ phone: p, purpose });
    setBusy(false);
    if (!resp || resp.status !== 200) {
      setError((resp && resp.data && resp.data.message) || '인증번호 발송에 실패했습니다.');
      return;
    }
    setStep('sent');
    setCode('');
    startTimer(Number(resp.data && resp.data.expiresIn) || 300);
  }, [phone, purpose, startTimer]);

  const verify = useCallback(async () => {
    const c = digits(code);
    if (c.length !== 6) { setError('인증번호 6자리를 입력해 주세요.'); return; }
    setBusy(true);
    setError('');
    const resp = await smsVerifyAPI({ phone: digits(phone), code: c });
    setBusy(false);
    if (!resp || resp.status !== 200 || !resp.data || !resp.data.uid) {
      setError((resp && resp.data && resp.data.message) || '인증번호 확인에 실패했습니다.');
      return;
    }
    stopTimer();
    setRemain(0);
    setStep('verified');
    if (onVerified) onVerified(resp.data.uid, resp.data.phone);
  }, [code, phone, onVerified, stopTimer]);

  const reset = useCallback(() => {
    stopTimer();
    setStep('idle');
    setCode('');
    setRemain(0);
    setError('');
  }, [stopTimer]);

  // 번호를 고치면 진행 중인 인증은 무효
  const changePhone = useCallback((value) => {
    setPhone(value);
    if (step !== 'idle') reset();
  }, [step, reset]);

  const changeCode = useCallback((value) => setCode(digits(value).slice(0, 6)), []);

  return { phone, code, step, busy, remain, error, changePhone, changeCode, send, verify, reset };
}
